// Model for human-in-the-loop queued edits: a proposed FHIR Bundle rendered
// as per-resource change sets a reviewer approves or rejects. Promoted from
// the @seen/react storybook prototype. See
// docs/human-in-the-loop-queued-edits.md.

import type { Resource } from '@medplum/fhirtypes'
import type { ExpectedOwnerResolution } from '#src/queued-edits/expected-owner.ts'

// A single FHIRPath patch operation, flattened for display in the technical
// panel. Carries semantic intent (`fhirPath` is element-keyed, e.g.
// `Patient.extension.where(url='X')`), unlike index-based JSON Patch.
export interface FhirPathPatchOp {
  op: 'add' | 'insert' | 'delete' | 'replace' | 'move'
  fhirPath: string
  value?: string
}

export type ResourceDecision = 'approved' | 'rejected'

// The mutation a bundle entry performs, mapped from its HTTP verb.
//   create (POST) · update (PUT/PATCH) · delete (DELETE)
export type ChangeVerb = 'create' | 'update' | 'delete'

export interface ScalarFieldChange {
  kind: 'scalar'
  label: string
  before: string | undefined
  after: string | undefined
  // The value at the pinned base version the proposal was drafted against
  // (`__seenBaseRef`). Populated only for drifted PATCH entries
  // (`changedSinceProposed`), so the reviewer can see what the proposer saw
  // alongside what is there now. Undefined when the field was absent at base.
  original?: string
  // Technical-only.
  fhirPath?: string
  rawBefore?: string
  rawAfter?: string
}

export type ArrayItemStatus = 'added' | 'removed' | 'modified' | 'unchanged'

export interface ArrayItem {
  status: ArrayItemStatus
  // Human-readable label, e.g. "Enrolled", "Medi-Cal Member ID ending ···381A".
  primary: string
  // The matching item's label at the pinned base version the proposal was
  // drafted against (`__seenBaseRef`). Populated only for drifted PATCH
  // entries (`changedSinceProposed`); undefined when no item with the same
  // identity existed at base.
  original?: string
  // System URL — technical-only.
  secondary?: string
  // Coding system chips (LOINC, SNOMED CT, HL7 v3, us-core) — technical-only.
  typeChips?: string[]
  // Raw FHIR code — technical-only.
  code?: string
  // Raw FHIR system — technical-only.
  system?: string
  // Inline before→after for sub-properties of a modified item.
  modifiedFields?: ScalarFieldChange[]
}

export interface ArrayFieldChange {
  kind: 'array'
  label: string
  fhirPath?: string
  items: ArrayItem[]
}

export type FieldChange = ScalarFieldChange | ArrayFieldChange

// One affected resource and the proposed change to it.
export interface ResourceChangeSet {
  // Stable decision key, unique per bundle entry (`entry-${index}`). Multiple
  // entries may touch the same resource — or POST resources with no id — so
  // the key is positional, not the resource reference.
  id: string
  // `ResourceType/id` of the affected resource, when known. Used to filter
  // the original bundle down to the approved entries at apply time.
  reference?: string
  // The bundle entry's fullUrl (`urn:uuid:…`). The deterministic handle for
  // POST entries, whose resources have no server id yet — builder-generated
  // bundles always set it.
  fullUrl?: string
  resourceType: string
  resourceId: string
  // `Patient/id` this resource belongs to, when derivable from the resource's
  // subject/for/participant. Lets the review UI deep-link the card into the
  // resource's patient-scoped detail page.
  patientReference?: string
  // The mutation kind (from the bundle entry's HTTP verb).
  verb: ChangeVerb
  // The resource the icon is derived from — the post-change resource for
  // create/update, the current resource for delete. Lets <ResourceIcon> refine
  // to a specific icon (e.g. imaging ServiceRequest → microscope). Optional;
  // falls back to the resourceType default.
  resource?: Resource
  // User-facing card title, e.g. "Coverage", "HbA1c Order".
  label: string
  // Proposer-supplied (AI/backend) intent: one short plain-English phrase for
  // WHY this entry changes (e.g. "Add the Onsite tag"). The card renders it in
  // place of the raw "N changes" count when present.
  intent?: string
  // Proposer-supplied group label (build-time, on the bundle entry). Entries
  // sharing a label render as one logical unit under a group header with
  // group-level approve/reject — e.g. a care plan with its goals,
  // interventions, and signature tasks.
  group?: string
  // ##ExpectedOwnerResolution The ownership rule for this individual change
  // (the task's assignee, the appointment's practitioner, or defer-to-PCP for
  // orders/care plans) — derived from current state at change-set time, never
  // stored on the proposal. Distinct from the decider: this is who is EXPECTED
  // to weigh in. PCP-kind resolutions need server lookups.
  expectedOwner?: ExpectedOwnerResolution
  // The entry's proposed change is already present in the current resource
  // state — the only remaining effect of applying it would be stamping the
  // queued-edits origin tag. The review surface drops these entries instead of
  // asking the reviewer to approve a no-op (someone made the change outside
  // the review after the proposal was built).
  alreadyApplied?: boolean
  // A domain path this entry's patch touches holds a different value now than
  // at the pinned version the proposal was drafted against (`__seenBaseRef`) —
  // the proposer never saw the diff being reviewed. The card flags it so the
  // reviewer looks closer; bookkeeping paths (meta tags, Task.lastModified)
  // never trigger this.
  changedSinceProposed?: boolean
  // Technical-only subtitle, e.g. resource id in mono.
  subtitle?: string
  changes: FieldChange[]
  // Underlying FHIRPath patch ops, shown in the technical panel.
  patchOps?: FhirPathPatchOp[]
  // Set when the proposed change could not be turned into a before → after
  // preview (patch failed to apply, missing current state, …). The card
  // renders a "could not preview" state instead of a wrong diff.
  conversionError?: string
}

// Flat row used by the expanded diff table. `flattenChanges` collapses both
// scalar and array field changes into uniform rows.
export interface FlatDiffRow {
  key: string
  label: string
  before: string | undefined
  after: string | undefined
  // The value at the pinned base version the proposal was drafted against.
  // Populated only for drifted PATCH entries (`changedSinceProposed`); the
  // expanded diff table renders it as an extra Original column in that case.
  original?: string
  status: ArrayItemStatus
  fhirPath?: string
  typeChips?: string[]
  code?: string
  system?: string
}

// Count a scalar change as a "field changing" only when before !== after.
function scalarChanged(c: ScalarFieldChange): boolean {
  return c.before !== c.after
}

// Number of fields actually changing — drives the "N fields changing" subtitle.
// Scalars count when their value changes; array fields count their non-unchanged
// items.
export function countChangedFields(changes: FieldChange[]): number {
  let count = 0
  for (const change of changes) {
    if (change.kind === 'scalar') {
      if (scalarChanged(change)) {
        count++
      }
    } else {
      count += change.items.filter((i) => i.status !== 'unchanged').length
    }
  }
  return count
}

// Decide a row's status from the presence/absence of its before and after
// values: only-after = added, only-before = removed, both-and-differ = modified.
function rowStatus(
  before: string | undefined,
  after: string | undefined
): ArrayItemStatus {
  const hasBefore = before !== undefined && before !== ''
  const hasAfter = after !== undefined && after !== ''
  if (hasBefore && hasAfter) {
    return before === after ? 'unchanged' : 'modified'
  }
  if (hasAfter) {
    return 'added'
  }
  if (hasBefore) {
    return 'removed'
  }
  return 'unchanged'
}

// Flatten all field changes into uniform diff rows.
// - Scalars become a single row; status derives from before/after presence
//   (added / removed / modified) so a create reads as all-add, a delete as
//   all-remove, and an edit as a mix.
// - Array items become one row each. Unchanged items are dropped: a diff of
//   one array element would otherwise render every sibling (e.g. all of a
//   resource's extensions) as identical before/after rows, drowning out the
//   actual change.
export function flattenChanges(changes: FieldChange[]): FlatDiffRow[] {
  const rows: FlatDiffRow[] = []

  for (const [changeIndex, change] of changes.entries()) {
    if (change.kind === 'scalar') {
      rows.push({
        key: `s-${changeIndex}-${change.label}`,
        label: change.label,
        before: change.before,
        after: change.after,
        original: change.original,
        status: rowStatus(change.before, change.after),
        fhirPath: change.fhirPath,
      })
      continue
    }

    for (const [itemIndex, item] of change.items.entries()) {
      if (item.status === 'unchanged') {
        continue
      }
      const before =
        item.status === 'added'
          ? undefined
          : (item.modifiedFields?.[0]?.before ?? item.primary)
      const after =
        item.status === 'removed'
          ? undefined
          : (item.modifiedFields?.[0]?.after ?? item.primary)

      rows.push({
        key: `a-${changeIndex}-${itemIndex}-${item.primary}`,
        label: change.label,
        before,
        after,
        original: item.original,
        status: item.status,
        fhirPath: change.fhirPath,
        typeChips: item.typeChips,
        code: item.code,
        system: item.system,
      })
    }
  }

  return rows
}
